import React from 'react';
import { toast } from 'sonner';
import { MessageCircle, Linkedin, Twitter, Instagram, Link2 } from 'lucide-react';

/**
 * Social share buttons for a blog article.
 * WhatsApp, LinkedIn and X open native share intents.
 * Instagram has no web share intent for arbitrary links, so we copy the URL
 * to the clipboard and prompt the user to paste it in their story/bio.
 */
export const ShareButtons = ({ title }) => {
  const url = typeof window !== 'undefined' ? window.location.href : '';
  const encodedUrl = encodeURIComponent(url);
  const encodedTitle = encodeURIComponent(title || '');

  const shareTargets = [
    {
      name: 'WhatsApp',
      testid: 'share-whatsapp',
      icon: MessageCircle,
      href: `https://wa.me/?text=${encodedTitle}%20${encodedUrl}`,
      className: 'bg-[#25D366] hover:bg-[#1ebe57]',
    },
    {
      name: 'LinkedIn',
      testid: 'share-linkedin',
      icon: Linkedin,
      href: `https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`,
      className: 'bg-[#0A66C2] hover:bg-[#08529b]',
    },
    {
      name: 'X',
      testid: 'share-x',
      icon: Twitter,
      href: `https://twitter.com/intent/tweet?text=${encodedTitle}&url=${encodedUrl}`,
      className: 'bg-black hover:bg-gray-800',
    },
  ];

  const copyForInstagram = async () => {
    try {
      await navigator.clipboard.writeText(url);
      toast.success('Enlace copiado. Pégalo en tu historia o bio de Instagram.');
    } catch {
      toast.error('No se pudo copiar el enlace.');
    }
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(url);
      toast.success('Enlace copiado al portapapeles.');
    } catch {
      toast.error('No se pudo copiar el enlace.');
    }
  };

  return (
    <div className="flex flex-wrap items-center gap-3" data-testid="share-buttons">
      <span className="text-sm font-semibold text-gray-600">Compartir:</span>
      {shareTargets.map(({ name, testid, icon: Icon, href, className }) => (
        <a
          key={name}
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={`Compartir en ${name}`}
          data-testid={testid}
          className={`w-10 h-10 flex items-center justify-center rounded-full text-white transition-all duration-300 hover:scale-110 ${className}`}
        >
          <Icon className="w-5 h-5" />
        </a>
      ))}
      <button
        type="button"
        onClick={copyForInstagram}
        aria-label="Compartir en Instagram"
        data-testid="share-instagram"
        className="w-10 h-10 flex items-center justify-center rounded-full text-white transition-all duration-300 hover:scale-110 bg-gradient-to-tr from-[#f09433] via-[#dc2743] to-[#bc1888]"
      >
        <Instagram className="w-5 h-5" />
      </button>
      <button
        type="button"
        onClick={copyLink}
        aria-label="Copiar enlace"
        data-testid="share-copy-link"
        className="w-10 h-10 flex items-center justify-center rounded-full text-gray-600 bg-gray-100 hover:bg-gray-200 transition-all duration-300 hover:scale-110"
      >
        <Link2 className="w-5 h-5" />
      </button>
    </div>
  );
};

export default ShareButtons;
